import React, { useMemo } from "react"

import { formatPrice, formatShortDate } from "../../../utils/utils"
import BadgeJs from "../../shared/Badge"
import HealMeTableJs from "../../shared/HealMeTable"
import { SearchInput } from "../../shared/Inputs"
import TypographyJs from "../../shared/Typography"

type AnyProps = Record<string, unknown> & { children?: React.ReactNode }
const Badge = BadgeJs as unknown as React.FC<AnyProps>
const HealMeTable = HealMeTableJs as unknown as React.FC<AnyProps>
const Typography = TypographyJs as unknown as React.FC<AnyProps>

export type CardChargeRow = {
  id: string
  capturedAt?: string | null
  createdAt: string
  amountCents: number
  amountCurrency: string
  note?: string | null
  state: string
  paymentMethod?: string | null
  lastError?: string | null
  client?: { firstName?: string | null; lastName?: string | null } | null
}

type CardChargesTableProps = {
  charges: CardChargeRow[]
  hideClientColumn: boolean
  showSearch?: boolean
  searchTerm?: string
  setSearchTerm?: (term: string) => void
}

const EMPTY_STATE = "No card charges yet. Use Charge card on a client's billing page to charge a custom amount."

const CardChargesTable: React.FC<CardChargesTableProps> = ({
  charges,
  hideClientColumn,
  showSearch = false,
  searchTerm,
  setSearchTerm
}) => {
  const columns = useMemo(() => {
    const cols = [
      {
        accessor: "createdAt",
        Header: "Date",
        Cell: (props: { cell: { row: { original: CardChargeRow } } }) => {
          const row = props.cell.row.original
          return formatShortDate(row.capturedAt || row.createdAt)
        }
      },
      {
        accessor: "client",
        Header: "Client",
        Cell: (props: { cell: { row: { original: CardChargeRow } } }) => {
          const client = props.cell.row.original.client
          return `${client?.firstName || ""} ${client?.lastName || ""}`.trim()
        }
      },
      {
        accessor: "note",
        Header: "Description",
        Cell: (props: { cell: { value?: string | null } }) => {
          const note = props.cell.value?.trim()
          return note || "Manual charge"
        }
      },
      {
        accessor: "amountCents",
        Header: "Amount",
        Cell: (props: { cell: { value: number; row: { original: CardChargeRow } } }) => {
          const { state, amountCurrency } = props.cell.row.original
          const textColorClass = state === "settled" ? "text-teal" : state === "failed" ? "text-red" : "text-gray-dark"
          return <span className={`sm:${textColorClass}`}>{formatPrice(props.cell.value || 0, amountCurrency)}</span>
        }
      },
      {
        accessor: "state",
        Header: "Status",
        className: "status",
        Cell: (props: { cell: { value: string; row: { original: CardChargeRow } } }) => {
          const state = props.cell.value
          let badgeType = "info"
          let label = state
          let paidBy: string | null = null
          if (state === "settled") {
            badgeType = "success"
            label = "Paid"
            paidBy = props.cell.row.original.paymentMethod || null
          } else if (state === "failed") {
            badgeType = "error"
            label = "Failed"
          }
          return (
            <>
              <Badge type={badgeType}>{label}</Badge>
              {paidBy && (
                <span className="ml-2 text-[12px] font-extrabold uppercase tracking-[0.045rem]">{paidBy}</span>
              )}
            </>
          )
        }
      },
      {
        accessor: "lastError",
        Header: "Payment note",
        className: "max-w-[14rem]",
        Cell: (props: { cell: { value?: string | null; row: { original: CardChargeRow } } }) => {
          const row = props.cell.row.original
          if (row.state !== "failed" || !row.lastError) {
            return <span className="text-gray-dark-light">—</span>
          }
          const note = row.lastError
          const short = note.length > 72 ? `${note.slice(0, 72)}…` : note
          return (
            <span className="text-xs leading-snug text-gray-dark" title={note}>
              {short}
            </span>
          )
        }
      }
    ]
    return hideClientColumn ? cols.filter((col) => col.accessor !== "client") : cols
  }, [hideClientColumn])

  if (charges.length === 0) {
    return (
      <div>
        {showSearch && (
          <SearchInput
            containerClassName=""
            value={searchTerm || ""}
            placeholder="Search by name or email"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm?.(e.target.value)}
          />
        )}
        <Typography variant="subtitle" className="mt-4 text-gray-dark">
          {EMPTY_STATE}
        </Typography>
      </div>
    )
  }

  return (
    <HealMeTable
      data={charges}
      columns={columns}
      hasPagination={false}
      showSearch={showSearch}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      searchPlaceholder="Search by name or email"
      searchType="card charges"
      header={() => null}
      rowClick={() => undefined}
    />
  )
}

export default CardChargesTable
